import { useEffect, useMemo, useState } from 'react';
import { apiFetch, useAuthStore } from '@/stores/authStore';
import { reconstructOrderTraderSide } from '@/lib/kalshiApi/orderReadV2';
import { mirrorCents } from '@/lib/pricing/subCent';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';
import { onActiveChange, subscribePush, topicActive } from '@/services/relayPush';
import { KALSHI_PLACEMENT_CONFIRMATION_TIMEOUT_MS } from '@/stores/accountStore';
import { setPlacementListener } from '@/lib/trading/placementBus';
import { QueueReceiptClock } from '@/lib/queueReceiptClock';
import { observeSourceTime } from '@/lib/sessionElapsedClock';

// Per-ticker view of the logged-in user's open Kalshi exposure.
// Positions are summed per side so the POS chip can render a proportional
// YES/NO arc when a user holds both, and each side carries the canonical
// before-fees average entry price behind that arc's hover (#161). Orders
// carry their Kalshi-native queue_position; cumulative "level + better" depth
// is computed at render time against the live orderbook (so it ticks with
// hydra without a refetch).
//
// SHARED POLLER (Jun 27): this hook is mounted at ~6 sites in a live window
// (Value Dashboard, three GameDetail sections, Spreads/Totals, Position
// Tracker), and each mount used to run its OWN /api/kalshi/me/snapshot poll
// every 5s — so one window fired the same request 4-6x over, multiplying the
// browser→relay HTTP/1.1 connection-pool pressure that makes extra windows
// "exponentially slower". Now a SINGLE module-level poller unions every
// subscriber's ticker needs into ONE /me/snapshot request and each hook
// filters the shared snapshot down to its OWN tickers — byte-identical output
// per consumer (no table ever sees another table's tickers), one request
// instead of N. Same within-window dedup pattern as useHeadroom.

export interface UserPositionSlice {
  yesCount: number;
  noCount: number;
  /** Canonical BEFORE-FEES average entry price for the held YES contracts,
   *  side-native cents at 0.01¢ grain — the same basis the Positions page
   *  shows, not a fee-adjusted or realized-P&L figure (#161).
   *  `null` means "not available", and is the ONLY way absence is expressed:
   *  the relay emits 0 as its no-basis value, so a 0 must never reach the UI
   *  as a price. Callers must say the average is unavailable — never print a
   *  number, and never silently drop the count along with it. */
  yesAvgCents: number | null;
  /** Same for the held NO contracts. */
  noAvgCents: number | null;
}

export interface UserOrderSlice {
  orderId: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  priceCents: number;
  remainingCount: number;
  queuePosition: number | null;
  /** Physical per-order source read, not the snapshot publication time. */
  queueObservedAtMs?: number;
  /** #666 display-lane provenance. Present only on `displayOrdersByTicker`
   *  rows: when the shown queue value was last confirmed by a live source
   *  reading, and whether it is being retained through a brief evidence gap
   *  rather than freshly confirmed. The strict `ordersByTicker` lane never
   *  carries retained values, so these stay undefined there. */
  queueConfirmedAtMs?: number | null;
  /** Browser-local monotonic provenance for elapsed-age tooltips. */
  queueConfirmedAtMonoMs?: number;
  queueRetained?: boolean;
  /** Positive per-order display evidence; never an automation permission. */
  queueDisplayConfirmation?: { value: number; confirmedAtMs: number };
}

export interface QueueSnapshotStatus {
  fresh: boolean;
  retained: boolean;
  fetchedAt: number | null;
  ageMs: number | null;
  staleAfterMs: number;
  reportedAt: number | null;
  consecutiveFailures: number;
  /** Local-only monotonic receipt time; source timestamps remain unchanged
   * for physical-reading identity and alert continuity. */
  receivedAtMonoMs?: number;
}

export interface UserKalshiPOS {
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  /** Human-facing order snapshot that retains the last confirmed queue value
   * while the relay's freshness contract is unavailable. This is display-only:
   * alerting, automation, and every money-adjacent decision must continue to
   * use `ordersByTicker` plus `queueStatus.fresh`. */
  displayOrdersByTicker: Map<string, UserOrderSlice[]>;
  loading: boolean;
  error: string | null;
  /** Relay-authoritative queue vintage. Null means a mixed-version relay that
   * predates the metadata contract; consumers preserve legacy behavior then. */
  queueStatus: QueueSnapshotStatus | null;
  refresh: () => Promise<void>;
  /**
   * Increments once per SNAPSHOT — a completed fetch, a pushed frame, or a
   * reset. Deliberately NOT a render or view counter: `ordersByTicker` is also
   * rebuilt when a consumer's ticker set changes (an order placed, cancelled,
   * or filled), which re-presents the SAME queue readings under a new Map.
   *
   * Consumers that must distinguish "a new reading arrived" from "the same
   * reading, re-delivered" have to read this rather than compare the Map — see
   * #275's queue-alert confirmation, where the difference decides whether a
   * lone oscillating sample is allowed to confirm itself.
   */
  snapshotSeq: number;
}

interface RawPosition {
  ticker?: unknown;
  side?: unknown;
  netCount?: unknown;
  /** Relay `OpenPosition` fields — already on every /me/snapshot and `pos`
   *  push frame; this hook simply did not read them before #161. */
  avgEntryPriceCents?: unknown;
  costBasisCents?: unknown;
}

interface RawOrder {
  order_id?: unknown;
  ticker?: unknown;
  outcome_side?: unknown;
  side?: unknown;
  action?: unknown;
  yes_price_dollars?: unknown;
  no_price_dollars?: unknown;
  remaining_count_fp?: unknown;
  yes_price?: unknown;
  no_price?: unknown;
  remaining_count?: unknown;
  queue_position_fp?: unknown;
  queue_position?: unknown;
  queue_position_observed_at?: unknown;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}
function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function asFloat(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function extractList<T>(payload: unknown, key: string): T[] {
  if (!payload || typeof payload !== 'object') return [];
  const list = (payload as Record<string, unknown>)[key];
  return Array.isArray(list) ? (list as T[]) : [];
}

function parseQueueStatus(payload: unknown, transportAgeMs = 0): QueueSnapshotStatus | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = (payload as Record<string, unknown>).queueStatus;
  if (!raw || typeof raw !== 'object') return null;
  const status = raw as Record<string, unknown>;
  const fetchedAt = asNumber(status.fetchedAt);
  const sourceAgeMs = asNumber(status.ageMs);
  const ageMs = sourceAgeMs === null || sourceAgeMs < 0 || !Number.isFinite(transportAgeMs)
    ? null : sourceAgeMs + transportAgeMs;
  const staleAfterMs = asNumber(status.staleAfterMs);
  const reportedAt = asNumber(status.reportedAt);
  return {
    // Once the new contract is present, only an explicit true is safe. A
    // partial/malformed status must degrade queue fields to unknown.
    fresh:
      status.fresh === true &&
      fetchedAt !== null &&
      ageMs !== null &&
      staleAfterMs !== null &&
      staleAfterMs > 0 &&
      reportedAt !== null &&
      ageMs <= staleAfterMs,
    retained: status.retained === true,
    fetchedAt,
    ageMs,
    // A present freshness contract without its expiry is incomplete. Keep the
    // status fail-closed rather than inventing a browser-only lifetime.
    staleAfterMs: staleAfterMs ?? 0,
    reportedAt,
    consecutiveFailures: asNumber(status.consecutiveFailures) ?? 0,
  };
}

const POLL_MS = 5000;
const SNAPSHOT_READ_TIMEOUT_MS = 10_000;
/** Human queue data remains useful for a brief operational gap, but an old
 * queue number eventually becomes less truthful than an explicit unknown. */
export const QUEUE_DISPLAY_MAX_AGE_MS = 30_000;

// ── module-level shared snapshot + poller ───────────────────────────────────
// One poll loop per browser window, shared across every mounted hook. The maps
// hold the UNION of all subscribers' tickers; each hook filters to its own.
let sharedPos = new Map<string, UserPositionSlice>();
let sharedOrders = new Map<string, UserOrderSlice[]>();
let sharedDisplayOrders = new Map<string, UserOrderSlice[]>();
let sharedQueueStatus: QueueSnapshotStatus | null = null;
const queueClock = new QueueReceiptClock();
let queueReceivedAtMs = 0;
let queueExpiryTimer: ReturnType<typeof setTimeout> | null = null;
let displayQueueExpiryTimer: ReturnType<typeof setTimeout> | null = null;
// Bumped beside EVERY `sharedOrders` assignment — see `snapshotSeq` above.
let snapshotSeq = 0;
let sharedError: string | null = null;
let sharedLoading = false;

interface Sub {
  tickers: ReadonlySet<string>;
  notify: () => void;
}
const subs = new Set<Sub>();
let stopPollTimer: (() => void) | null = null;
let inFlight = false;
let pendingRefetch = false;
// Bumped on logout/reset. A fetch captures the epoch before its await and
// discards its result if the epoch moved — so an in-flight poll that started as
// User A can never write A's positions into the cache AFTER a logout wiped it.
let epoch = 0;

function notifyAll(): void {
  for (const s of subs) s.notify();
}

function clearQueueExpiryTimer(): void {
  if (queueExpiryTimer !== null) clearTimeout(queueExpiryTimer);
  if (displayQueueExpiryTimer !== null) clearTimeout(displayQueueExpiryTimer);
  queueExpiryTimer = null;
  displayQueueExpiryTimer = null;
}

function expireSharedQueue(expectedSnapshotSeq: number): void {
  if (snapshotSeq !== expectedSnapshotSeq || sharedQueueStatus?.fresh !== true) return;
  let changed = false;
  const nextOrders = new Map<string, UserOrderSlice[]>();
  for (const [ticker, orders] of sharedOrders) {
    const next = orders.map((order) => {
      if (order.queuePosition == null) return order;
      changed = true;
      return { ...order, queuePosition: null };
    });
    nextOrders.set(ticker, next);
  }
  sharedQueueStatus = {
    ...sharedQueueStatus,
    fresh: false,
  };
  if (changed) sharedOrders = nextOrders;
  // #779 — the reading just aged out locally. On a stream-fed tab that is the
  // moment nothing is left to recover it, so arm the fallback poll.
  reconcilePolling();
  // This is a local freshness transition, not a new source reading. Keep the
  // #275 snapshotSeq contract exact; notifyAll is sufficient to re-render the
  // null queue values and reset queue-derived alert state.
  notifyAll();
}

// ── #666 — per-order display-queue confirmation ledger ─────────────────────
//
// The old display retention keyed on the GLOBAL snapshot status: it engaged
// only when a whole frame arrived not-fresh. Two real production transitions
// bypassed it and flashed a still-resting chip to `?` inside the 30s human
// horizon (Kevin's CHC/SEA recording):
//   • a FRESH frame whose merged orders simply lack one order's queue fields
//     (the relay's #652 per-id retention hands off to honest-unknown after
//     its 4s bound — the venue omission can outlive it);
//   • a frame with missing/malformed queueStatus metadata (legacy shape),
//     which skipped the retention branch entirely.
// The ledger tracks WHEN each order's queue value was last confirmed by a
// live reading, so display continuity is per-order and survives both cases,
// while the strict lane keeps failing closed exactly as before.
const displayQueueConfirmations = new Map<
  string,
  { value: number; confirmedAtMs: number }
>();

function clearDisplayQueueConfirmations(): void {
  displayQueueConfirmations.clear();
}

/** Build the human display lane from the authoritative order list: rows with
 * a live queue value pass through (and refresh the ledger caller-side); rows
 * missing one are filled from their own in-horizon confirmation, marked
 * `queueRetained`. Order identity, price, remaining size, and lifecycle all
 * come from `next` — an order absent from `next` renders nowhere, and its
 * ledger entry has already been pruned by the caller, so a cancelled, filled,
 * or replaced order can never resurrect from the display cache. */
function buildDisplayOrders(
  next: Map<string, UserOrderSlice[]>,
  now: number,
): Map<string, UserOrderSlice[]> {
  const out = new Map<string, UserOrderSlice[]>();
  for (const [ticker, orders] of next) {
    out.set(
      ticker,
      orders.map((order) => {
        if (order.queuePosition != null) {
          const confirmedAtMs = order.queueDisplayConfirmation?.confirmedAtMs ?? now;
          return { ...order, queueConfirmedAtMs: confirmedAtMs,
            queueConfirmedAtMonoMs: performance.now() - (now - confirmedAtMs), queueRetained: false };
        }
        const confirmation = displayQueueConfirmations.get(order.orderId);
        if (
          !confirmation ||
          now - confirmation.confirmedAtMs > QUEUE_DISPLAY_MAX_AGE_MS
        ) {
          // Never confirmed (new order) or beyond the human horizon —
          // explicit unknown is the truthful state.
          return order;
        }
        return {
          ...order,
          queuePosition: confirmation.value,
          queueConfirmedAtMs: confirmation.confirmedAtMs,
          queueConfirmedAtMonoMs: performance.now() - (now - confirmation.confirmedAtMs),
          queueRetained: true,
        };
      }),
    );
  }
  return out;
}

/** Expire display rows whose retained confirmation crossed the horizon. Runs
 * from the display timer between frames; every applied frame rebuilds and
 * reschedules, so this only matters across quiet gaps. */
function expireSharedDisplayQueue(expectedSnapshotSeq: number): void {
  if (snapshotSeq !== expectedSnapshotSeq) return;
  const now = queueClock.now();
  let changed = false;
  const nextOrders = new Map<string, UserOrderSlice[]>();
  for (const [ticker, orders] of sharedDisplayOrders) {
    const next = orders.map((order) => {
      // Fresh-confirmed rows age too: a silent network gap after a healthy
      // frame must still surface explicit unknown at the horizon, exactly
      // like a retained row (the pre-ledger behavior for a stalled poll).
      if (order.queuePosition == null || order.queueConfirmedAtMs == null) return order;
      if (now - order.queueConfirmedAtMs <= QUEUE_DISPLAY_MAX_AGE_MS) return order;
      changed = true;
      return { ...order, queuePosition: null, queueRetained: false };
    });
    nextOrders.set(ticker, next);
  }
  for (const [id, confirmation] of displayQueueConfirmations) {
    if (now - confirmation.confirmedAtMs > QUEUE_DISPLAY_MAX_AGE_MS) {
      displayQueueConfirmations.delete(id);
    }
  }
  if (!changed) {
    scheduleDisplayQueueExpiry();
    return;
  }
  sharedDisplayOrders = nextOrders;
  scheduleDisplayQueueExpiry();
  notifyAll();
}

/** Arm the display expiry for the EARLIEST retained row still on screen —
 * per-order horizons replaced the old single global deadline. No retained
 * rows → no timer (a later frame that introduces one reschedules). */
function scheduleDisplayQueueExpiry(): void {
  if (displayQueueExpiryTimer !== null) clearTimeout(displayQueueExpiryTimer);
  displayQueueExpiryTimer = null;
  const now = queueClock.now();
  let earliestRemaining = Infinity;
  for (const orders of sharedDisplayOrders.values()) {
    for (const order of orders) {
      if (order.queuePosition == null || order.queueConfirmedAtMs == null) continue;
      const remaining = QUEUE_DISPLAY_MAX_AGE_MS - (now - order.queueConfirmedAtMs);
      if (remaining < earliestRemaining) earliestRemaining = remaining;
    }
  }
  if (!Number.isFinite(earliestRemaining)) return;
  const expectedSnapshotSeq = snapshotSeq;
  displayQueueExpiryTimer = setTimeout(() => {
    displayQueueExpiryTimer = null;
    expireSharedDisplayQueue(expectedSnapshotSeq);
  }, Math.max(0, earliestRemaining) + 1);
}

function scheduleQueueExpiry(): void {
  clearQueueExpiryTimer();
  // Display expiry is per-order now (#666) — armed from the retained rows
  // themselves, not the global status age.
  scheduleDisplayQueueExpiry();
  const status = sharedQueueStatus;
  if (!status) return;
  const ageAtReceiptMs = (status.ageMs ?? QUEUE_DISPLAY_MAX_AGE_MS) +
    Math.max(0, queueClock.now() - queueReceivedAtMs);
  const expectedSnapshotSeq = snapshotSeq;
  if (status.fresh) {
    const remainingMs = status.staleAfterMs - ageAtReceiptMs;
    queueExpiryTimer = setTimeout(() => {
      queueExpiryTimer = null;
      expireSharedQueue(expectedSnapshotSeq);
    }, Math.max(0, remainingMs) + 1);
  }
}


// ── Placement projection ────────────────────────────────────────────────────
//
// A venue-accepted resting order used to wait for the next `pos` frame (or the
// 5 s poll) before the ladder/GameTable/Game Detail markers painted it —
// accountStore's `_placementConfirmations` fence protects only its own
// `openOrders` list. This is the same discipline for THIS lane: placeOrder's
// success path publishes the accepted resting order via placementBus and it
// renders immediately, then dies the moment an authoritative snapshot carries
// its id (the venue row wins), when the store reports it gone (cancel, fill,
// lifecycle), when the shared confirmation horizon expires, or on cache reset.
// Projections are keyed per orderId and inherit NOTHING across ids.
// `queuePosition` is pinned null — a projection must never invent queue
// evidence (buildDisplayOrders renders it as an honest unknown).
interface PlacementProjection {
  ticker: string;
  slice: UserOrderSlice;
  expiresAtMs: number;
  timer: ReturnType<typeof setTimeout>;
}
const placementProjections = new Map<string, PlacementProjection>();

function deletePlacementProjection(orderId: string): void {
  const p = placementProjections.get(orderId);
  if (!p) return;
  clearTimeout(p.timer);
  placementProjections.delete(orderId);
}

function clearPlacementProjections(): void {
  for (const id of [...placementProjections.keys()]) deletePlacementProjection(id);
}

/** Union the surviving (unexpired) projections into an authoritative orders
 * map. Returns the input map unchanged when no projection applies; never
 * mutates it. Callers prune confirmed/expired projections first, so a ticker
 * list can never hold both the authoritative row and its projection. */
function withProjections(
  orders: Map<string, UserOrderSlice[]>,
  nowMs: number,
): Map<string, UserOrderSlice[]> {
  if (placementProjections.size === 0) return orders;
  let out: Map<string, UserOrderSlice[]> | null = null;
  for (const p of placementProjections.values()) {
    if (p.expiresAtMs <= nowMs) continue;
    if (!out) out = new Map(orders);
    out.set(p.ticker, [...(out.get(p.ticker) ?? []), p.slice]);
  }
  return out ?? orders;
}

/** Strip a projection's row from both lanes. A confirmed id was already
 * replaced by the venue row in applyParsedSnapshot, so any row still carrying
 * this id IS the projection. */
function stripProjectedRow(orderId: string, ticker: string): void {
  const strip = (m: Map<string, UserOrderSlice[]>): Map<string, UserOrderSlice[]> => {
    const list = m.get(ticker);
    if (!list || !list.some((o) => o.orderId === orderId)) return m;
    const out = new Map(m);
    const next = list.filter((o) => o.orderId !== orderId);
    if (next.length > 0) out.set(ticker, next);
    else out.delete(ticker);
    return out;
  };
  sharedOrders = strip(sharedOrders);
  sharedDisplayOrders = strip(sharedDisplayOrders);
}

/** The projection outlived its horizon with no authoritative confirmation —
 * remove its row so the UI cannot keep showing an order the venue never
 * proved. */
function expirePlacementProjection(orderId: string): void {
  const p = placementProjections.get(orderId);
  if (!p) return;
  deletePlacementProjection(orderId);
  stripProjectedRow(orderId, p.ticker);
  notifyAll();
}

/** Record a venue-accepted resting placement so it renders NOW, ahead of the
 * next authoritative snapshot. Safe with zero subscribers (notifyAll over an
 * empty set; the next mount's snapshot merge re-presents survivors). */
export function recordUserPosPlacement(
  ticker: string,
  slice: UserOrderSlice,
  nowMs: number = Date.now(),
): void {
  // Never carry queue fields on a projection, whatever the caller passed.
  const projected: UserOrderSlice = {
    orderId: slice.orderId,
    side: slice.side,
    action: slice.action,
    priceCents: slice.priceCents,
    remainingCount: slice.remainingCount,
    queuePosition: null,
  };
  deletePlacementProjection(projected.orderId); // re-place: replace + re-arm
  const expiresAtMs = nowMs + KALSHI_PLACEMENT_CONFIRMATION_TIMEOUT_MS;
  const timer = setTimeout(
    () => expirePlacementProjection(projected.orderId),
    Math.max(0, expiresAtMs - Date.now()) + 1,
  );
  placementProjections.set(projected.orderId, { ticker, slice: projected, expiresAtMs, timer });
  // Surface immediately in both lanes without touching authoritative rows.
  const add = (m: Map<string, UserOrderSlice[]>): Map<string, UserOrderSlice[]> => {
    const out = new Map(m);
    out.set(ticker, [
      ...(out.get(ticker) ?? []).filter((o) => o.orderId !== projected.orderId),
      projected,
    ]);
    return out;
  };
  sharedOrders = add(sharedOrders);
  sharedDisplayOrders = add(sharedDisplayOrders);
  notifyAll();
}

/** The store learned the order is no longer resting (venue-accepted cancel,
 * draining fill, or the cancel lifecycle confirming absence). Drop the
 * projection at once rather than letting it stand until the horizon; an
 * authoritative row for the same id is left untouched (the snapshot owns it). */
export function forgetUserPosPlacement(orderId: string): void {
  const p = placementProjections.get(orderId);
  if (!p) return;
  deletePlacementProjection(orderId);
  stripProjectedRow(orderId, p.ticker);
  notifyAll();
}

// accountStore's write paths publish here (no import cycle — see placementBus).
setPlacementListener({
  placed: (event) =>
    recordUserPosPlacement(event.ticker, {
      orderId: event.orderId,
      side: event.side,
      action: event.action,
      priceCents: event.priceCents,
      remainingCount: event.remainingCount,
      queuePosition: null,
    }),
  removed: (orderId) => forgetUserPosPlacement(orderId),
});

function applyParsedSnapshot(parsed: ReturnType<typeof parseSnapshot>): void {
  sharedPos = parsed.pos;
  const now = queueClock.now();
  queueReceivedAtMs = now;
  // Refresh the confirmation ledger from this frame's LIVE values. Values are
  // present only when the source reading was usable (fresh status, or a
  // legacy null-status relay whose values were always trusted), so a
  // not-fresh frame updates nothing here. Discount the reported source age so
  // a value that was already old at the relay does not restart its horizon.
  const sourceAgeMs =
    parsed.queueStatus?.ageMs != null && parsed.queueStatus.reportedAt != null
      ? Math.min(
          QUEUE_DISPLAY_MAX_AGE_MS,
          parsed.queueStatus.ageMs,
        )
      : 0;
  const liveOrderIds = new Set<string>();
  for (const orders of parsed.orders.values()) {
    for (const order of orders) {
      liveOrderIds.add(order.orderId);
      if (order.queueDisplayConfirmation) {
        const previous = displayQueueConfirmations.get(order.orderId);
        if (!previous || order.queueDisplayConfirmation.confirmedAtMs >= previous.confirmedAtMs) {
          displayQueueConfirmations.set(order.orderId, order.queueDisplayConfirmation);
        }
      } else if (order.queuePosition != null) {
        displayQueueConfirmations.set(order.orderId, {
          value: order.queuePosition,
          confirmedAtMs: now - sourceAgeMs,
        });
      }
    }
  }
  // Lifecycle prune: an order absent from the authoritative order list is
  // cancelled/filled/replaced. Its confirmation dies with it, immediately.
  for (const id of [...displayQueueConfirmations.keys()]) {
    if (!liveOrderIds.has(id)) displayQueueConfirmations.delete(id);
  }
  // Drop projections this frame confirms (the authoritative row wins, same
  // discipline as the liveOrderIds prune above) or that outlived the placement
  // horizon, then union the survivors so a just-placed order stays visible
  // across frames that lag the write.
  for (const id of [...placementProjections.keys()]) {
    const p = placementProjections.get(id)!;
    if (liveOrderIds.has(id) || p.expiresAtMs <= Date.now()) deletePlacementProjection(id);
  }
  queueClock.retain(liveOrderIds);
  const withProjected = withProjections(parsed.orders, Date.now());
  sharedDisplayOrders = buildDisplayOrders(withProjected, now);
  sharedOrders = withProjected;
  sharedQueueStatus = parsed.queueStatus;
  // #850 — the relay's clock runs on another machine, so it is independent of
  // this one's. Successive readings of it outrunning our monotonic clock are
  // the only evidence available that the host slept while the tab stayed
  // VISIBLE, where no visibility event ever fires. Reporting it is all that
  // happens here: the elapsed clock owns what counts as a discontinuity, and
  // no queue, freshness, polling or display decision on this path reads it.
  if (parsed.queueStatus?.reportedAt != null) {
    observeSourceTime(parsed.queueStatus.reportedAt);
  }
  snapshotSeq += 1;
  scheduleQueueExpiry();
  // #779 — queue health just moved; a stream-fed tab may now need (or no
  // longer need) its fallback poll.
  reconcilePolling();
}

/** Union of all NON-EMPTY subscriber ticker sets, sorted. Empty subscribers
 *  (a table with no visible Kalshi tickers / no open orders) contribute nothing
 *  and render nothing — we deliberately do NOT widen to a no-filter "fetch all"
 *  poll, because the relay only enriches queue_position when market_tickers is
 *  supplied, so an all-poll would strip queue data from every other consumer. */
function unionTickers(): string[] {
  const u = new Set<string>();
  for (const s of subs) for (const t of s.tickers) u.add(t);
  return Array.from(u).sort();
}

/** Exported for tests (sub-cent open-order fixtures). */
export function parseSnapshot(json: unknown, receipt?: { now: number; transportAgeMs: number; clock: QueueReceiptClock }): {
  pos: Map<string, UserPositionSlice>;
  orders: Map<string, UserOrderSlice[]>;
  queueStatus: QueueSnapshotStatus | null;
} {
  const rawPositions = extractList<RawPosition>(json, 'positions');
  const rawOrders = extractList<RawOrder>(json, 'orders');
  const queueStatus = parseQueueStatus(json, receipt?.transportAgeMs ?? 0);
  if (queueStatus && receipt) queueStatus.receivedAtMonoMs = performance.now();
  const queueUsable = queueStatus === null || queueStatus.fresh;

  const posMap = new Map<string, UserPositionSlice>();
  // Entry basis per (ticker, side), folded into the slice below. We sum COST
  // and COUNT rather than averaging the averages, so a side that arrives as
  // several rows still yields the contracts-weighted average — the identical
  // fold the relay runs (positions.ts / positionStates.ts), which means a
  // single-row side reproduces its own `avgEntryPriceCents` exactly and this
  // hover can never disagree with the Positions page for the same position.
  const basis = new Map<string, { cost: number; count: number; known: boolean }>();

  for (const p of rawPositions) {
    const ticker = asString(p.ticker);
    const side = asString(p.side);
    const net = asNumber(p.netCount);
    if (!ticker || !net || net <= 0) continue;
    if (side !== 'yes' && side !== 'no') continue;
    const slice = posMap.get(ticker) ?? {
      yesCount: 0,
      noCount: 0,
      yesAvgCents: null,
      noAvgCents: null,
    };
    if (side === 'yes') slice.yesCount += net;
    else slice.noCount += net;
    posMap.set(ticker, slice);

    // Prefer the exact held-cost basis over avg × count — the house rule
    // everywhere else that spends this payload (PositionTracker, OrgTradersTable,
    // unrealizedCoverage): the avg is grain-rounded and re-multiplying it drifts
    // the cost on positions built from many fractional fills (I-035).
    const acc = basis.get(`${ticker}|${side}`) ?? { cost: 0, count: 0, known: true };
    const costBasis = asNumber(p.costBasisCents);
    const avg = asNumber(p.avgEntryPriceCents);
    const rowCost = costBasis ?? (avg != null ? avg * net : null);
    // One unusable row poisons the whole side: a partial average computed from
    // only the rows that happened to carry a basis would be a confident wrong
    // number, which is worse on a money surface than an honest dash.
    if (rowCost == null) acc.known = false;
    else acc.cost += rowCost;
    acc.count += net;
    basis.set(`${ticker}|${side}`, acc);
  }

  for (const [key, acc] of basis) {
    const sep = key.lastIndexOf('|');
    const slice = posMap.get(key.slice(0, sep));
    if (!slice) continue;
    // 0.01¢ grain — byte-identical to the relay's own avgEntryPriceCents fold.
    // Whole-cent rounding here would redisplay a 56.9¢ entry as 57¢, the exact
    // regression caught in prod on 2026-07-17.
    const avg =
      acc.known && acc.count > 0 ? Math.round((acc.cost / acc.count) * 100) / 100 : null;
    // Non-positive is treated as UNAVAILABLE, never rendered as a price. Two
    // ways a 0 arises: the relay's own avg is 0 whenever it has no count to
    // divide by (netCount <= 0 — rows already dropped above), and a real
    // position whose basis rounds below 0.005¢ lands on 0 here. Neither is a
    // price anyone paid, and "0¢" reads as "you paid nothing" — hence the
    // ticket's "truthful fallback rather than zero".
    const value = avg != null && avg > 0 ? avg : null;
    if (key.slice(sep + 1) === 'yes') slice.yesAvgCents = value;
    else slice.noAvgCents = value;
  }

  const ordMap = new Map<string, UserOrderSlice[]>();
  for (const o of rawOrders) {
    const orderId = asString(o.order_id);
    const ticker = asString(o.ticker);
    // Reconstruct the trader-native {side, action} for DISPLAY. V2 is single-
    // book: a resting buy-NO reads back side:'yes'/action:'sell'/outcome_side:'no',
    // so reading legacy `side` first mislabeled the overlay's NO orders as YES
    // on the wrong ladder leg. reconstructOrderTraderSide derives side from
    // outcome_side and flips action when cross-framed — the same adapter
    // parseOrders uses. Orders only; fills keep the legacy pair.
    const { side: sideRaw, action: actionRaw } = reconstructOrderTraderSide(o);
    const remaining =
      asFloat(o.remaining_count_fp) ?? asNumber(o.remaining_count) ?? 0;
    if (!orderId || !ticker || remaining <= 0) continue;
    const yesDollars = asFloat(o.yes_price_dollars);
    const noDollars = asFloat(o.no_price_dollars);
    // Centi-cent grain (0.01¢) — these prices become exact-match ladder map
    // keys downstream (GameTable POS overlay); whole-cent rounding made a
    // resting 58.25¢ order miss its own 58.25¢ level.
    let yesPx = yesDollars != null ? Math.round(yesDollars * 10000) / 100 : asNumber(o.yes_price);
    let noPx = noDollars != null ? Math.round(noDollars * 10000) / 100 : asNumber(o.no_price);
    // Kalshi frequently sends only ONE leg's price — a NO order commonly
    // arrives as `yes_price` only (CLAUDE.md venue quirk: "derive 100 − yes_price
    // when the side-matching field is missing or zero"). Derive the missing/zero
    // side via the EXACT mirror so a resting NO order isn't silently dropped
    // from the POS overlay. Mirrors PositionSidebar / accountStore / TraderDetail.
    if ((yesPx == null || yesPx === 0) && noPx != null && noPx !== 0) yesPx = mirrorCents(noPx);
    if ((noPx == null || noPx === 0) && yesPx != null && yesPx !== 0) noPx = mirrorCents(yesPx);
    const priceCents = sideRaw === 'yes' ? yesPx : noPx;
    if (priceCents == null) continue;
    const queueValue = asFloat(o.queue_position_fp) ?? asNumber(o.queue_position);
    const observedAt = asNumber(o.queue_position_observed_at);
    const reportedAt = queueStatus?.reportedAt ?? null;
    const sourceAge = observedAt === null || reportedAt === null ? null : reportedAt - observedAt;
    const now = receipt?.now ?? Date.now();
    const candidate = sourceAge === null || sourceAge < 0 ? null : now - sourceAge - (receipt?.transportAgeMs ?? 0);
    const confirmedAt = candidate === null || queueValue === null || queueValue < 0 ? null : receipt
      ? receipt.clock.confirm(orderId, observedAt!, candidate) : candidate;
    const observedAge = confirmedAt === null ? null : now - confirmedAt;
    const queueDisplayConfirmation = observedAge !== null && observedAge >= 0 &&
      observedAge <= QUEUE_DISPLAY_MAX_AGE_MS && queueValue !== null && queueValue >= 0
      ? { value: queueValue, confirmedAtMs: confirmedAt! } : undefined;
    const rowEvidenceUsable = o.queue_position_observed_at === undefined ||
      (observedAge !== null && observedAge >= 0 && observedAge <= (queueStatus?.staleAfterMs ?? 0));
    const queuePosition = queueUsable && rowEvidenceUsable
      ? queueValue
      : null;
    const slice: UserOrderSlice = {
      orderId,
      side: sideRaw,
      action: actionRaw,
      priceCents,
      remainingCount: remaining,
      queuePosition,
      ...(observedAt != null ? { queueObservedAtMs: observedAt } : {}),
      ...(queueDisplayConfirmation ? { queueDisplayConfirmation } : {}),
    };
    const list = ordMap.get(ticker) ?? [];
    list.push(slice);
    ordMap.set(ticker, list);
  }

  return { pos: posMap, orders: ordMap, queueStatus };
}

/** Bound both headers and body decoding. Abort is cleanup, not the liveness
 * guarantee: a stalled transport may settle late or never settle at all. Only
 * the winning read returns data to the account/queue application path. */
async function readSnapshot(url: string): Promise<{ status: number; ok: boolean; json?: unknown }> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async () => {
        const res = await apiFetch(url, { cache: 'no-store', signal: controller.signal });
        if (controller.signal.aborted) throw new Error('Snapshot read expired');
        const json: unknown = res.ok ? await res.json() : undefined;
        if (controller.signal.aborted) throw new Error('Snapshot read expired');
        return { status: res.status, ok: res.ok, json };
      })(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error('Activity refresh timed out; retrying'));
          controller.abort();
        }, SNAPSHOT_READ_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function fetchOnce(): Promise<void> {
  // Single-flight: never stack concurrent snapshots. If asked again mid-flight,
  // remember to re-poll once the current one settles (covers a just-added
  // ticker set without waiting a full interval).
  if (inFlight) {
    pendingRefetch = true;
    return;
  }
  const union = unionTickers();
  if (union.length === 0) {
    // No subscriber needs any ticker — present empty, skip the network call.
    // Only notify when there was data to clear (avoids an idle every-5s churn).
    if (sharedPos.size || sharedOrders.size) {
      sharedPos = new Map();
      sharedOrders = new Map();
      sharedDisplayOrders = new Map();
      clearDisplayQueueConfirmations();
      sharedQueueStatus = null;
      clearQueueExpiryTimer();
      snapshotSeq += 1;
      sharedError = null;
      notifyAll();
    }
    return;
  }
  inFlight = true;
  sharedLoading = true;
  const myEpoch = epoch;
  const requestStarted = queueClock.now();
  const requestClockGeneration = queueClock.generation;
  try {
    const url = `/api/kalshi/me/snapshot?market_tickers=${encodeURIComponent(union.join(','))}`;
    const res = await readSnapshot(url);
    if (myEpoch !== epoch) return; // a logout/reset raced us — discard this result.
    if (!res.ok) {
      // apiFetch does NOT throw on non-2xx. A 401/429/5xx must KEEP the last
      // snapshot, not blank it — a confidently-empty positions/orders overlay on
      // a money surface is the dangerous failure. Surface the error instead.
      sharedError = `snapshot failed (${res.status})`;
      return;
    }
    const json: unknown = res.json;
    if (myEpoch !== epoch) return;
    if (requestClockGeneration !== queueClock.generation) {
      pendingRefetch = true; // reconnect/resume invalidated this request's time bound
      return;
    }
    const parsed = parseReceivedSnapshot(json, requestStarted);
    applyParsedSnapshot(parsed);
    sharedError = null;
  } catch (err) {
    if (myEpoch !== epoch) return;
    // Keep the last snapshot on a thrown (network) error too — don't blank.
    sharedError = err instanceof Error ? err.message : 'Failed to load activity';
  } finally {
    // Only release the single-flight latch + drive UI if we're STILL the current
    // epoch. A stale-epoch fetch (a logout/reset moved epoch while we were in
    // flight) must NOT clear a live re-login fetch's inFlight — reset already
    // cleared it — else single-flight breaks for one tick across that race.
    if (myEpoch === epoch) {
      inFlight = false;
      sharedLoading = false;
      reconcilePolling();
      notifyAll();
      if (pendingRefetch) {
        pendingRefetch = false;
        void fetchOnce();
      }
    }
  }
}

/** #779 — is the queue reading currently unhealthy, so a stream-fed tab still
 *  needs its fallback poll?
 *
 *  A stream-fed tab stands its poll down completely, and the relay re-emits
 *  `pos` only on the org queue poller's SUCCESS path. While that lane is
 *  degraded nothing reaches the tab at all: no frame to deliver a recovery,
 *  and no poll to go asking for one. The trader is left reloading the page —
 *  a new connection is snapshot-on-subscribe — which is exactly the "full
 *  page reload" the ticket forbids as a recovery route.
 *
 *  A null status alone preserves mixed-version behavior. A failed read still
 *  arms recovery, including a cold start whose first snapshot never arrived. */
function queueNeedsFallbackPoll(): boolean {
  return sharedError !== null || (sharedQueueStatus != null && sharedQueueStatus.fresh !== true);
}

function ensurePolling(): void {
  if (stopPollTimer) return;
  // Push-fed when the relay stream serves the pos topic (org-scope users);
  // the poll timer only arms as fallback (incl. personal-key scope, which
  // the server marks unavailable) — or while the queue reading is unhealthy
  // and only a request of our own can discover the recovery (#779).
  if (topicActive('pos') && !queueNeedsFallbackPoll()) return;
  // Hidden tabs poll the me/snapshot slice at 1/6 rate; immediate on refocus.
  stopPollTimer = startVisibilityAwareInterval(() => void fetchOnce(), POLL_MS);
}

/** #779 — decide the poll from BOTH inputs (stream availability and queue
 *  health) rather than from stream availability alone, and re-decide wherever
 *  either one moves. `ensurePolling` on its own only ever ran at mount and on
 *  a stream up/down, so a queue that went stale mid-session could never arm
 *  the fallback that would recover it.
 *
 *  Only WHEN we ask changes. No displayed value is invented, so #553's
 *  fail-visible contract is untouched. The timer it arms is this hook's own
 *  existing poll — single-flight via `inFlight`/`pendingRefetch` (89437fb5),
 *  visibility-throttled to 1/6 in hidden tabs (4257b88b) — so no new request
 *  path is introduced and its worst case equals the poll-fed lane's normal
 *  case. #552's bounds are RELAY-side (the org queue poller) and are not
 *  reachable from here at all. A healthy stream still stands the poll down,
 *  so push keeps its bandwidth win in the ordinary case. */
function reconcilePolling(): void {
  if (subs.size === 0) {
    stopPolling();
    return;
  }
  if (topicActive('pos') && !queueNeedsFallbackPoll()) stopPolling();
  else ensurePolling();
}
function stopPolling(): void {
  if (stopPollTimer) {
    stopPollTimer();
    stopPollTimer = null;
  }
}

// Stream-fed slice: frames are the /api/kalshi/me/snapshot shape (queue
// merged into orders) for the user's FULL slice — parseSnapshot applies
// unchanged and subscribers keep filtering to their own tickers locally.
function parseReceivedSnapshot(payload: unknown, requestStarted?: number) {
  const status = parseQueueStatus(payload);
  const transportAgeMs = status === null ? 0 : queueClock.transportAge(status.reportedAt, requestStarted);
  return parseSnapshot(payload, { now: queueClock.now(), transportAgeMs, clock: queueClock });
}

subscribePush('pos', (payload) => {
  const parsed = parseReceivedSnapshot(payload);
  applyParsedSnapshot(parsed);
  sharedError = null;
  reconcilePolling();
  notifyAll();
});
onActiveChange(() => {
  if (!topicActive('pos')) queueClock.invalidate();
  reconcilePolling();
});

// Some platforms stop their monotonic clock while asleep. Recalibrate after
// a detected discontinuity, not every ordinary tab switch (which would flash
// otherwise valid queue readings). Orders/positions never depend on this clock.
let hiddenClock: { wall: number; mono: number } | null = null;
if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') {
    hiddenClock = { wall: Date.now(), mono: performance.now() };
    return;
  }
  const hidden = hiddenClock;
  hiddenClock = null;
  if (!hidden || subs.size === 0 || Math.abs(
    (Date.now() - hidden.wall) - (performance.now() - hidden.mono)
  ) < 1_000) return;
  queueClock.invalidate();
  clearDisplayQueueConfirmations();
  sharedOrders = new Map([...sharedOrders].map(([t, rows]) => [t, rows.map(o => ({ ...o, queuePosition: null, queueDisplayConfirmation: undefined }))]));
  sharedDisplayOrders = sharedOrders;
  if (sharedQueueStatus) sharedQueueStatus = { ...sharedQueueStatus, fresh: false };
  clearQueueExpiryTimer();
  reconcilePolling();
  notifyAll();
  void fetchOnce();
});

/** Wipe the shared snapshot on logout so a different user never reads stale
 *  positions/orders. Mirrors resetHeadroomCache — wired into authStore logout. */
export function resetUserKalshiPOSCache(): void {
  epoch += 1; // invalidate any in-flight fetch so it can't write post-reset.
  hiddenClock = null;
  queueClock.reset();
  sharedPos = new Map();
  sharedOrders = new Map();
  sharedDisplayOrders = new Map();
  clearDisplayQueueConfirmations();
  clearPlacementProjections(); // a projection must not cross sessions
  sharedQueueStatus = null;
  clearQueueExpiryTimer();
  // A wipe IS a new snapshot: without the bump, a candidate raised before a
  // logout could confirm against the first reading after the next login.
  snapshotSeq += 1;
  sharedError = null;
  inFlight = false;
  sharedLoading = false;
  pendingRefetch = false;
  stopPolling();
  // Do NOT clear `subs` — the auth-gated hook effects own their subscribe/
  // unsubscribe lifecycle; clearing here would desync subs.size from the live
  // mounts. On re-auth they re-subscribe and ensurePolling() restarts the loop.
}

function filterToOwn<V>(m: Map<string, V>, own: ReadonlySet<string>): Map<string, V> {
  const out = new Map<string, V>();
  for (const t of own) {
    const v = m.get(t);
    if (v !== undefined) out.set(t, v);
  }
  return out;
}

export function useUserKalshiPOS(tickers: string[]): UserKalshiPOS {
  // Gate on auth like the sibling shared pollers (useHeadroom / useRateLimitStatus)
  // so the loop never fires /me/snapshot while logged out (no stale-token polls)
  // and so a logout tears the subscription down.
  const isAuthed = useAuthStore((s) => s.status === 'authenticated');
  const tickerKey = useMemo(() => tickers.slice().sort().join('|'), [tickers]);
  // Re-render trigger: the shared poller bumps this on every settled snapshot.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isAuthed) return;
    const set: ReadonlySet<string> = new Set(tickerKey ? tickerKey.split('|') : []);
    const sub: Sub = { tickers: set, notify: () => setTick((t) => t + 1) };
    subs.add(sub);
    reconcilePolling();
    // Cover this subscriber's tickers promptly — a freshly-opened game/table
    // shouldn't wait up to 5s for its positions/orders.
    void fetchOnce();
    // Surface whatever's already cached without waiting for the next poll.
    setTick((t) => t + 1);
    return () => {
      subs.delete(sub);
      if (subs.size === 0) stopPolling();
    };
  }, [tickerKey, isAuthed]);

  const ownSet = useMemo(
    () => new Set(tickerKey ? tickerKey.split('|') : []),
    [tickerKey],
  );

  // Filter the shared snapshot down to THIS consumer's tickers — byte-identical
  // to the old per-mount hook (no consumer ever sees another table's tickers).
  // `tick` in the deps recomputes the view when a fresh snapshot lands; sharedPos
  // /sharedOrders are module-level (non-reactive) so they're intentionally not deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const positionByTicker = useMemo(() => filterToOwn(sharedPos, ownSet), [ownSet, tick]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ordersByTicker = useMemo(() => filterToOwn(sharedOrders, ownSet), [ownSet, tick]);
  // Display-only vintage lane: stable through a healthy-but-slower queue
  // refresh, while `ordersByTicker` continues to expire fail-closed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const displayOrdersByTicker = useMemo(
    () => filterToOwn(sharedDisplayOrders, ownSet),
    [ownSet, tick],
  );

  return {
    positionByTicker,
    ordersByTicker,
    displayOrdersByTicker,
    loading: sharedLoading,
    error: sharedError,
    queueStatus: sharedQueueStatus,
    refresh: fetchOnce,
    // Read at render time. The fetch and push paths call notifyAll(), so a
    // render is already scheduled and this value is the one that render belongs
    // to. `resetUserKalshiPOSCache` deliberately does NOT notify — it runs at
    // logout, which unmounts the consumers anyway; the bump is there so the
    // next session cannot inherit the previous one's reading identity.
    snapshotSeq,
  };
}
